import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn } from "../components/UI";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, Circle, Sparkles, ArrowRight, PartyPopper, HandCoins, Tv,
} from "lucide-react";

const INTENT_OPTIONS = [
  { key: "fundraise", label: "Raise money first", icon: HandCoins, hint: "Focus on donation pages, microsites, events + your finance team." },
  { key: "advertise", label: "Reach voters first", icon: Tv, hint: "Focus on brand, targeting and launching your first ad campaign." },
  { key: "both", label: "Do both", icon: Sparkles, hint: "Show every step — fundraising and advertising side by side." },
];

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (intent) => {
    const q = intent ? `?intent=${encodeURIComponent(intent)}` : "";
    return api.get(`/political-os/onboarding/checklist${q}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(errMsg(e, "Could not load checklist")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const pickIntent = async (intent) => {
    setLoading(true);
    await load(intent);
    // Auto-hide the OTHER intent's nav groups so the sidebar shows only what
    // this user actually asked for. They can always add features back via the
    // sidebar "+ Add features" pill or the group-header "+" chip.
    try {
      const HIDE_MAP = {
        fundraise: [
          "nav-create-campaign", "nav-ctv", "nav-ctv-planner", "nav-campaigns", "nav-media-plans",
          "nav-email", "nav-direct-mail", "nav-journeys",
          "nav-ad-studio", "nav-ad-refreshes", "nav-ai-assistant", "nav-creative-studio",
          "nav-ai-landing", "nav-creatives",
          "nav-target-voters", "nav-political-data", "nav-audiences", "nav-first-party",
          "nav-geo", "nav-other-targeting", "nav-ad-library",
        ],
        advertise: [
          "nav-fundraising", "nav-fr-campaigns", "nav-landing-pages", "nav-microsites",
          "nav-donors", "nav-transactions", "nav-source-economics", "nav-sms",
          "nav-events", "nav-volunteers", "nav-volunteer-leaderboard",
        ],
        both: [],
      };
      const toHide = HIDE_MAP[intent] || [];
      if (toHide.length) {
        await api.post("/app-settings/hidden-features", { hidden: toHide });
        toast.success(`Focus set — showing only ${intent === "both" ? "every step" : intent} features. Refresh to see the sidebar update.`);
      } else {
        toast.success("Focus set — showing every feature in the sidebar.");
        await api.post("/app-settings/hidden-features", { hidden: [] });
      }
    } catch {
      /* non-fatal — intent still applied to checklist */
    }
  };

  if (loading || !data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  if (!data.has_entity) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="onboarding-no-entity">
        <PageHeader eyebrow="Getting Started" title="Set up your political entity first" subtitle="TotalReach needs to know which campaign you represent before we can start the onboarding checklist." />
        <div className="mt-4">
          <Btn onClick={() => navigate("/onboarding/political")} data-testid="checklist-start-onboarding-btn">
            <Sparkles className="w-4 h-4" /> Start onboarding
          </Btn>
        </div>
      </div>
    );
  }

  const pct = Math.round((data.progress || 0) * 100);

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-6" data-testid="onboarding-checklist-page">
      <PageHeader
        eyebrow="Getting Started"
        title={data.complete ? `${data.entity_name} is fully launched` : `Pre-launch Checklist for ${data.entity_name}`}
        subtitle={data.complete
          ? "Every core setup step is done — you're ready to run this race."
          : "The launch steps you actually need — no busywork. Every one unlocks a real capability."}
      />

      {/* Intent picker — filters visible steps by user's stated goal */}
      {!data.complete && (
        <Card data-testid="prelaunch-intent-picker">
          <div className="text-xs uppercase tracking-widest text-cyan-300 font-semibold mb-1">
            What do you want to launch first?
          </div>
          <div className="text-xs text-slate-400 mb-3">
            Pick a focus and we&apos;ll only show the steps that matter for that path. You can add the rest later from the sidebar &quot;+&quot; menu.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {INTENT_OPTIONS.map((opt) => {
              const active = (data.intent || "").toLowerCase() === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => pickIntent(opt.key)}
                  data-testid={`prelaunch-intent-${opt.key}`}
                  className={`text-left rounded-xl border p-3 transition-colors ${
                    active
                      ? "border-cyan-400/50 bg-cyan-400/10"
                      : "border-white/10 hover:border-cyan-400/40 hover:bg-white/[0.02]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <opt.icon className={`w-4 h-4 ${active ? "text-cyan-300" : "text-slate-400"}`} />
                    <div className={`text-sm font-semibold ${active ? "text-white" : "text-slate-200"}`}>{opt.label}</div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 leading-snug">{opt.hint}</div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {data.complete ? (
        <Card data-testid="checklist-complete-card" className="border-emerald-400/40 bg-emerald-400/5">
          <div className="flex items-start gap-3">
            <PartyPopper className="w-8 h-8 text-emerald-300 shrink-0" />
            <div>
              <div className="font-display text-lg font-bold text-white">You&apos;re fully set up.</div>
              <div className="text-sm text-slate-300 mt-1">
                Every step in the checklist is done. Head to your dashboard to run the campaign, or open the AI Chief of Staff to start planning next week.
              </div>
              <div className="flex gap-2 mt-3">
                <Btn onClick={() => navigate("/dashboard")} data-testid="checklist-open-dashboard-btn">
                  Open dashboard
                </Btn>
                <Btn onClick={() => navigate("/ask-totalreach")} variant="secondary" data-testid="checklist-ask-btn">
                  Ask TotalReach
                </Btn>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <Card data-testid="checklist-progress-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Progress</div>
              <div className="font-display text-2xl font-bold text-white mt-1" data-testid="checklist-progress-count">
                {data.done_count} / {data.total_steps} <span className="text-slate-500 text-base font-medium">complete</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">Verification score</div>
              <div className="text-cyan-300 font-bold text-xl">{data.verification_score || 0}<span className="text-xs text-slate-500 font-normal">/100</span></div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden" data-testid="checklist-progress-bar">
            <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          {data.next_action && (
            <div className="mt-4 flex items-center justify-between p-3 rounded-lg bg-cyan-400/5 border border-cyan-400/30">
              <div className="text-sm text-slate-200">Next up: <span className="font-semibold text-white">{data.next_action.label}</span></div>
              <button onClick={() => navigate(data.next_action.url)} data-testid="checklist-next-action-btn"
                className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold inline-flex items-center gap-1">
                Continue <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </Card>
      )}

      <Card padded={false} data-testid="checklist-steps-card">
        <div className="divide-y divide-white/5">
          {(data.steps || []).map((s) => (
            <div key={s.key} className="p-5 flex items-start gap-4" data-testid={`checklist-step-${s.key}`}>
              <div className="shrink-0 mt-0.5">
                {s.done
                  ? <CheckCircle2 className="w-6 h-6 text-emerald-400" data-testid={`checklist-step-done-${s.key}`} />
                  : <Circle className="w-6 h-6 text-slate-600" />}
              </div>
              <div className="flex-1">
                <div className={`font-display text-base font-semibold ${s.done ? "text-slate-400 line-through decoration-slate-700" : "text-white"}`}>
                  {s.label}
                </div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed">{s.description}</div>
              </div>
              {!s.done && (
                <button onClick={() => navigate(s.action_url)} data-testid={`checklist-step-cta-${s.key}`}
                  className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10 inline-flex items-center gap-1">
                  {s.cta} <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
